import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AddInIds from "@/constants/AddInIds";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useRemoveAddIn } from "@/hooks/addins/useRemoveAddIn";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";

type ExternalLinkDetails = {
  id: "external-link";
  url: string;
};

const CONTENT_MIN_HEIGHT = 120;

const ManageExternalLink: React.FC = () => {
  const { t } = useTranslation();
  const addInId = AddInIds.externalLink;
  const {
    data: items = [],
    isLoading,
    error,
  } = useAddinData<ExternalLinkDetails>(addInId);

  const stored = useMemo(() => items.find(i => i.id === "external-link") ?? null, [items]);

  const [value, setValue] = useState<string>("");

  useEffect(() => {
    setValue(stored?.url ?? "");
  }, [stored?.url]);

  const saveMutation = useUpdateAddIn<ExternalLinkDetails>(addInId, "id");
  const removeMutation = useRemoveAddIn<ExternalLinkDetails>(addInId, "id");

  const [touched, setTouched] = useState(false);
  const trimmed = value.trim();
  const urlIsValid = useMemo(() => {
    if (!trimmed) return false;
    try {
      const u = new URL(trimmed);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  }, [trimmed]);

  const isBusy = saveMutation.isPending || removeMutation.isPending;

  const handleSave = async () => {
    setTouched(true);
    if (!urlIsValid) return;

    await saveMutation.mutateAsync({
      id: "external-link",
      url: trimmed,
    });
  };

  const handleRemove = async () => {
    await removeMutation.mutateAsync({ delSearch: "external-link" });
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, minHeight: CONTENT_MIN_HEIGHT }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: CONTENT_MIN_HEIGHT }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{t("admin.externalLink.loadError")}</Typography>
        ) : (
          <Stack spacing={2}>
            <TextField
              label={t("admin.externalLink.label")}
              placeholder="https://example.com/..."
              value={value}
              onChange={e => setValue(e.target.value)}
              onBlur={() => setTouched(true)}
              fullWidth
              size="small"
              disabled={isBusy}
              error={touched && !!trimmed && !urlIsValid}
              helperText={
                touched && trimmed && !urlIsValid ? t("admin.externalLink.invalidUrl") : " "
              }
              InputProps={{
                endAdornment: isBusy ? <CircularProgress size={18} /> : null,
              }}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={isBusy || !urlIsValid || trimmed === (stored?.url ?? "")}
              >
                {t("admin.externalLink.save")}
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleRemove}
                disabled={isBusy || !stored}
              >
                {t("admin.externalLink.remove")}
              </Button>
            </Stack>

            {stored?.url ? (
              <Typography variant="body2">
                {t("admin.externalLink.currentValue")}{" "}
                <strong>{stored.url}</strong>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("admin.externalLink.noValue")}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ManageExternalLink;
